import type { CurrentUserQuery } from '@/graphql/queries/currentUserQuery.gql.generated'
import { Action, PermissionEffect, Subject } from '@/graphql/schema'

type Workspace = CurrentUserQuery['currentUser']['workspace']
type CurrentUser = CurrentUserQuery['currentUser']
type NonNullableCurrentUser = NonNullable<CurrentUser>

type PermissionAction =
  | 'access'
  | 'configure'
  | 'create'
  | 'delete'
  | 'export'
  | 'import'
  | 'invite'
  | 'manage'
  | 'read'
  | 'transfer'
  | 'update'

type PermissionSubject =
  | 'all'
  | 'auditLog'
  | 'automation'
  | 'communication'
  | 'communicationChannel'
  | 'communicationTemplate'
  | 'recordStorage'
  | 'entityDefinition'
  | 'exportJob'
  | 'importJob'
  | 'mcp'
  | 'permission'
  | 'records'
  | 'role'
  | 'template'
  | 'view'
  | 'workspace'
  | 'workspaceMembership'
  | 'workspaceSettings'

export type CurrentUserPermissionAction = PermissionAction
export type CurrentUserPermissionSubject = PermissionSubject
export type CurrentUserCan = (
  action: PermissionAction,
  subject: PermissionSubject,
) => boolean
export type CurrentUserWithPermissions = NonNullableCurrentUser & {
  can: CurrentUserCan
}

const ACTION_BY_INPUT = {
  access: Action.Access,
  configure: Action.Configure,
  create: Action.Create,
  delete: Action.Delete,
  export: Action.Export,
  import: Action.Import,
  invite: Action.Invite,
  manage: Action.Manage,
  read: Action.Read,
  transfer: Action.Transfer,
  update: Action.Update,
} as const satisfies Record<PermissionAction, Action>

const SUBJECT_BY_INPUT = {
  all: Subject.All,
  auditLog: Subject.AuditLog,
  automation: Subject.Automation,
  communication: Subject.Communication,
  communicationChannel: Subject.CommunicationChannel,
  communicationTemplate: Subject.CommunicationTemplate,
  recordStorage: Subject.RecordStorage,
  entityDefinition: Subject.EntityDefinition,
  exportJob: Subject.ExportJob,
  importJob: Subject.ImportJob,
  mcp: Subject.Mcp,
  permission: Subject.Permission,
  records: Subject.Records,
  role: Subject.Role,
  template: Subject.Template,
  view: Subject.View,
  workspace: Subject.Workspace,
  workspaceMembership: Subject.WorkspaceMembership,
  workspaceSettings: Subject.WorkspaceSettings,
} as const satisfies Record<PermissionSubject, Subject>

function matchesPermission({
  action,
  permissionAction,
  permissionSubject,
  subject,
}: {
  action: Action
  permissionAction: Action
  permissionSubject: Subject
  subject: Subject
}) {
  return (
    (permissionAction === action || permissionAction === Action.Manage) &&
    (permissionSubject === subject || permissionSubject === Subject.All)
  )
}

function isAlwaysDenied(action: Action, subject: Subject) {
  return (
    subject === Subject.AuditLog &&
    (action === Action.Update || action === Action.Delete)
  )
}

function canWorkspacePerform(
  workspace: Workspace | null | undefined,
  action: PermissionAction,
  subject: PermissionSubject,
) {
  if (!workspace) {
    return false
  }

  const requestedAction = ACTION_BY_INPUT[action]
  const requestedSubject = SUBJECT_BY_INPUT[subject]

  if (isAlwaysDenied(requestedAction, requestedSubject)) {
    return false
  }

  const hasDeny = workspace.permissions.some(
    (permission) =>
      permission.effect === PermissionEffect.Deny &&
      matchesPermission({
        action: requestedAction,
        permissionAction: permission.action,
        permissionSubject: permission.subject,
        subject: requestedSubject,
      }),
  )

  if (hasDeny) {
    return false
  }

  if (
    requestedAction === Action.Read &&
    requestedSubject === Subject.Workspace
  ) {
    return true
  }

  return workspace.permissions.some(
    (permission) =>
      permission.effect === PermissionEffect.Allow &&
      matchesPermission({
        action: requestedAction,
        permissionAction: permission.action,
        permissionSubject: permission.subject,
        subject: requestedSubject,
      }),
  )
}

export function canCurrentUserPerform(
  currentUser: CurrentUser | null | undefined,
  action: PermissionAction,
  subject: PermissionSubject,
) {
  return canWorkspacePerform(currentUser?.workspace, action, subject)
}

export function decorateCurrentUserPermissions(
  currentUser: CurrentUser | null | undefined,
): CurrentUserWithPermissions | null {
  if (!currentUser) {
    return null
  }

  return {
    ...currentUser,
    can: (action, subject) =>
      canCurrentUserPerform(currentUser, action, subject),
  }
}

export function canCreateRecords(workspace: Workspace | null | undefined) {
  return canWorkspacePerform(workspace, 'create', 'records')
}
